import React, { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Send, Loader, Building2, User, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const StyledInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-12 w-full rounded-md border border-purple-100 bg-purple-50/30 px-3 py-2 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
StyledInput.displayName = "StyledInput";
const StyledTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-md border border-purple-100 bg-purple-50/30 px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
StyledTextarea.displayName = "StyledTextarea";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="47110" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 animate-in fade-in zoom-in duration-500">
                <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-6 shadow-sm">
                    <Send className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">提交成功</h3>
                <p className="text-muted-foreground mb-8 max-w-md">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="border-primary/20 text-primary hover:bg-primary/5">
                    再次提交查詢
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "請輸入您的姓名"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">聯絡人姓名 <span className="text-destructive">*</span></FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <User className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/40" />
                                    <StyledInput placeholder="陳大文" className="pl-10" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "請輸入電郵地址",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "請輸入有效的電郵地址"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">電郵地址 <span className="text-destructive">*</span></FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/40" />
                                    <StyledInput type="email" placeholder="info@example.com" className="pl-10" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9-+]{5,20}$/,
          message: "請輸入有效的電話號碼"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">聯絡電話</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/40" />
                                    <StyledInput type="tel" placeholder="5343 0380" className="pl-10" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">服務對象關係</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Building2 className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground/40" />
                                    <StyledInput placeholder="例如：本人 / 子女 / 配偶" className="pl-10" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "請輸入您的查詢內容",
      minLength: {
        value: 20,
        message: "請至少輸入20個字，以便我們更好地理解您的需求"
      },
      maxLength: {
        value: 1000,
        message: "內容不能超過1000字"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground/90">查詢內容或服務需求 <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                            <div className="relative">
                                <FileText className="absolute left-3 top-4 h-5 w-5 text-muted-foreground/40" />
                                <StyledTextarea placeholder="請簡述服務對象的狀況及所需服務，例如：居家護理、到戶物理治療等..." className="pl-10 resize-none" {...field} />
                            </div>
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                    <p className="text-destructive text-sm">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-lg shadow-lg shadow-primary/20 transition-all hover:shadow-xl hover:shadow-primary/30">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 h-5 w-5" />
                        提交中...
                    </> : '提交查詢'}
            </Button>
        </Form>;
}
export default function ContactStars() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="即時聯繫管道" className="bg-background text-foreground py-24 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-full h-1/2 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-secondary/40 via-background to-background pointer-events-none" />
            
            <div className="container mx-auto px-4 relative z-10">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    
                    {}
                    <div ref={ref} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                        <div className="space-y-4">
                            <h2 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                                即時聯繫管道
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed">
                                開啟您的居家護理支援之旅。不論是服務查詢、專業諮詢或預約到戶評估，憬芯醫療團隊隨時準備為您提供協助。
                            </p>
                        </div>

                        <div className="grid gap-6">
                            {}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Card className="bg-card/80 border-purple-100 hover:bg-card hover:shadow-xl hover:shadow-purple-200/40 transition-all cursor-pointer group overflow-hidden relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                        <CardContent className="p-6 flex items-center gap-6 relative z-10">
                                            <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm shadow-primary/10">
                                                <MessageCircle className="h-7 w-7 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">微信諮詢</h3>
                                                <p className="text-sm text-muted-foreground">掃描二維碼添加專屬客服</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-0 border-purple-100 bg-white/95 backdrop-blur-xl shadow-2xl">
                                    <div className="p-6 flex flex-col items-center text-center space-y-4">
                                        <div className="relative w-48 h-48 bg-white rounded-lg p-2 border border-purple-50">
                                            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606170/img/wechat_qr_code_contact.png" alt="WeChat QR Code" className="w-full h-full object-contain" />
                                        </div>
                                        <p className="text-sm text-muted-foreground">掃一掃，快速溝通</p>
                                    </div>
                                </PopoverContent>
                            </Popover>

                            {}
                            <Link to="tel:53430380" className="block">
                                <Card className="bg-card/80 border-purple-100 hover:bg-card hover:shadow-xl hover:shadow-purple-200/40 transition-all cursor-pointer group overflow-hidden relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-secondary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <CardContent className="p-6 flex items-center gap-6 relative z-10">
                                        <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm shadow-secondary-foreground/10">
                                            <Phone className="h-7 w-7 text-secondary-foreground" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">電話諮詢</h3>
                                            <p className="text-sm text-muted-foreground">5343 0380 (服務專線)</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>

                            {}
                            <Card className="bg-card/80 border-purple-100 hover:bg-card hover:shadow-xl hover:shadow-purple-200/40 transition-all group overflow-hidden relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <CardContent className="p-6 flex items-center gap-6 relative z-10">
                                    <div className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm shadow-accent-foreground/10">
                                        <MapPin className="h-7 w-7 text-accent-foreground" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">辦公地址</h3>
                                        <p className="text-sm text-muted-foreground">九龍紅磡馬頭圍道37號紅磡廣場1樓98A鋪</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {}
                    <div className="lg:col-span-7">
                        <div className="relative">
                            {}
                            <div className="absolute inset-0 bg-white/60 backdrop-blur-xl rounded-3xl border border-purple-100 shadow-2xl shadow-purple-200/30" />
                            
                            {}
                            <div className="absolute -top-10 -right-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none opacity-50" />
                            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-secondary/30 rounded-full blur-3xl pointer-events-none opacity-50" />

                            <div className="relative z-10 p-8 md:p-10">
                                <div className="mb-8">
                                    <h3 className="text-2xl font-bold text-foreground mb-2">提交服務查詢</h3>
                                    <p className="text-muted-foreground">
                                        請填寫下表，我們專業的醫護顧問將於 24 小時內與您聯絡並安排評估。
                                    </p>
                                </div>

                                <FormProvider formKey="instant_contact_form_contact_us" formId="instant_contact_form_contact_us" sectionName="contact-stars-section" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  pattern: "phone"
                },
                message: {
                  required: true,
                  minLength: 20,
                  maxLength: 1000
                }
              }} submitText="提交查詢" successMessage="感謝您的留言！我們的顧問團隊會盡快與您聯繫。" errorMessage="提交失敗，請檢查網絡後重試。">
                                    <ContactFormContent />
                                </FormProvider>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}